import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useRequestContext } from "../../hooks/useRequest";
import { Collapsible, StepPayments } from "../../../components";
import { useHistory } from "react-router-dom";
import { requestTypes } from "../../constants";
import { SectionTrigger } from "../../../orderManagement/molecules/SectionTrigger";
import {
  SwitchXeroOrgModal,
  orgLabel,
} from "../../../orderManagement/atoms/SwitchXeroOrgModal";
import { setRequestXeroOptions } from "../../../hooks/useXeroBrandingThemes";
import { pxSellSectionId } from "../../v2/sectionConfig";
import { useEntitlement } from "../../../contexts";

export function Payments({ step, idx, navigateTo }) {
  const history = useHistory();
  const [listing, setListing] = useState(null);
  const {
    getContext,
    afterAddingPrice,
    toggleStep,
    requestType,
    refreshRequest,
  } = useRequestContext();
  // Step-aware so a PX group's per-sell-request instance records the payment
  // against ITS trade-in, not against the parent order request.
  const { request } = getContext(idx, step);

  // ── Xero organisation (multi-org) ────────────────────────────────────────
  // Which connected org this sell request's Purchase Order is generated in.
  // Persisted onto the request so the PURCHASE ORDER section picks it up; locked
  // once a PO exists.
  const [xeroOrganisations, setXeroOrganisations] = useState([]);
  const [xeroConnectionId, setXeroConnectionId] = useState(
    request?.xero_connection_id ?? null
  );
  const poLocked = !!request?.xero_purchase_order_id;

  // A PO org switch awaiting confirmation in SwitchXeroOrgModal. Null = no
  // dialog. See onSelectXeroOrg.
  const [xeroOrgSwitch, setXeroOrgSwitch] = useState(null);

  const cancelXeroOrgSwitch = () => {
    if (xeroOrgSwitch?.submitting) return;
    setXeroConnectionId(xeroOrgSwitch?.fromId ?? null); // snap the picker back
    setXeroOrgSwitch(null);
  };

  const confirmXeroOrgSwitch = async () => {
    const pending = xeroOrgSwitch;
    if (!pending || pending.submitting) return;

    setXeroOrgSwitch({ ...pending, submitting: true, error: null });

    try {
      await axios.post(
        `/api/requests/${request.id}/switch-xero-org`,
        { xero_connection_id: pending.toId },
        // The dialog owns the messaging for this action — no duplicate toast.
        { metadata: { silent: true } }
      );
    } catch (e) {
      // Previously this catch reverted the picker and said NOTHING, so a refusal
      // Xero had explained in detail ("Cannot void a Purchase Order that is
      // already BILLED in Xero") read as the switch silently doing nothing.
      setXeroOrgSwitch({
        ...pending,
        submitting: false,
        error:
          e?.response?.data?.message ||
          "Couldn't switch Xero organisation for this purchase order.",
      });
      return;
    }

    // Branding themes belong to the organisation they were read from, so a pick
    // made under the old org cannot be stamped on a PO in the new one. The
    // server clears it on the switch; mirror that locally so the picker shows
    // the new org's default straight away. Only after the switch has actually
    // gone through — a cancelled or failed one changes nothing.
    setXeroBrandingThemeId(null);
    setXeroOrgSwitch(null);
    await refreshRequest?.();
  };

  // Plan-gated route: a store without Xero POs gets our own PO PDF, has no
  // org to pick, and would only earn a "not in your plan" toast by asking.
  const hasXeroPo = useEntitlement("xero_purchase_orders");
  useEffect(() => {
    if (!hasXeroPo) return undefined;
    let active = true;
    axios
      .get("/api/xero/organisations")
      .then(({ data }) => {
        if (active) setXeroOrganisations(data?.connections || []);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [hasXeroPo]);

  // Sell flow deliberately does NOT auto-default the org — the rep must pick one
  // on the PAYMENTS section before a purchase order can be generated. Only sync
  // from an already-pinned value (e.g. on reload / after persisting).
  useEffect(() => {
    if (request?.xero_connection_id) {
      setXeroConnectionId(request.xero_connection_id);
    }
  }, [request?.xero_connection_id]);

  // ── Billing address ────────────────────────────────────────────────────────
  // Which of the SUPPLIER's saved addresses the purchase order is billed to.
  // Null = their default address. StepPayments has rendered the picker since the
  // address book landed, but nothing supplied these props, so it never appeared
  // in the sell flow — wired up here.
  const [xeroAddressId, setXeroAddressId] = useState(
    request?.xero_address_id ?? null
  );

  useEffect(() => {
    setXeroAddressId(request?.xero_address_id ?? null);
  }, [request?.xero_address_id]);

  const onSelectXeroAddress = async (addressId) => {
    const prev = xeroAddressId;
    setXeroAddressId(addressId);
    if (!request?.id) return;
    try {
      await setRequestXeroOptions(request.id, { xero_address_id: addressId });
      await refreshRequest?.();
    } catch (e) {
      setXeroAddressId(prev ?? null);
    }
  };

  // ── Branding theme (Xero letterhead) ───────────────────────────────────────
  // Which theme the PURCHASE ORDER is generated with. Null = the organisation's
  // default, which is what every PO carried before this picker existed.
  const [xeroBrandingThemeId, setXeroBrandingThemeId] = useState(
    request?.xero_branding_theme_id ?? null
  );

  useEffect(() => {
    setXeroBrandingThemeId(request?.xero_branding_theme_id ?? null);
  }, [request?.xero_branding_theme_id]);

  // Persisted immediately, like the org pick, so it is on the request by the
  // time GENERATE PURCHASE ORDER runs. Unlike the org it is NOT locked once a
  // PO exists — re-generating under a corrected letterhead is the point of it.
  const onSelectXeroBranding = async (themeId) => {
    const prev = xeroBrandingThemeId;
    setXeroBrandingThemeId(themeId);
    if (!request?.id) return;
    try {
      await setRequestXeroOptions(request.id, {
        xero_branding_theme_id: themeId,
      });
      await refreshRequest?.();
    } catch (e) {
      setXeroBrandingThemeId(prev ?? null);
    }
  };

  const onSelectXeroOrg = async (id) => {
    const prev = request?.xero_connection_id ?? xeroConnectionId;
    setXeroConnectionId(id); // optimistic — keeps the dropdown in sync
    if (!request?.id || !id || Number(id) === Number(prev)) return;

    // A PO already exists → switching voids it in the current org and re-creates
    // it in the new one. Confirmed in SwitchXeroOrgModal (rendered below) rather
    // than a browser pop-up, which named neither organisation nor the document.
    if (poLocked) {
      // A PO carries a Xero UUID (xero_purchase_order_id), not a human-facing
      // number like an invoice does — so there is nothing to name here, and the
      // dialog shows the from → to organisations alone.
      setXeroOrgSwitch({
        fromId: prev ?? null,
        toId: id,
        submitting: false,
        error: null,
      });
      return;
    }

    // No PO yet — just pin the choice.
    //
    // Through the narrow xero-options endpoint, NOT `PUT /api/requests/{id}`:
    // that route used to rebuild the record from the payload, so pinning an org
    // this way also NULLed the request's `year`, `price_expectation` /
    // `delivery_set` and forced `is_direct` (it now writes only the keys sent,
    // L10). Picking a dropdown must not edit the deal.
    try {
      await setRequestXeroOptions(request.id, {
        xero_connection_id: id,
        // The old org's theme cannot apply to the new one.
        xero_branding_theme_id: null,
      });
      setXeroBrandingThemeId(null);
      await refreshRequest?.();
    } catch {
      /* keep local selection even if persist fails; PO call also sends it */
    }
  };

  // Resolve the primary listing from whichever source wired it up.
  // Database path: a quote was created and a listing made from it
  // (quote.listing_id set) — fetch via the quote. Active-Listings path:
  // the rep linked an existing listing directly (request.linked_listing_id)
  // without ever creating a listing from the quote — fetch that listing.
  // Keying on quoteListingId (not just last_quote_id) is what lets the
  // Active-Listings flow surface the DEPOSIT field without forcing the
  // rep to link the listing back to the quote.
  const resolveListing = async () => {
    const quoteListingId = request?.last_quote?.listing_id;
    const primaryListingId = quoteListingId ?? request?.linked_listing_id ?? null;
    if (!primaryListingId) return;
    try {
      const { data } = quoteListingId
        ? await axios.post(`/api/quotes/${request.last_quote_id}/listing`)
        : await axios.get(`/api/listings/${primaryListingId}`);
      setListing(data);
    } catch {
      setListing(null);
    }
  };

  const onSave = async (deposits) => {
    if (requestType === requestTypes.STOCK) {
      afterAddingPrice(deposits);
    } else {
      const saved = await axios.put(`/api/requests/${request.id}/add-price`, {
        ...deposits,
      });
      // A 5xx resolves undefined (global interceptor, which already showed
      // the error). Stop here: the deposits were NOT saved, so do not mark
      // the step done or move on to the Purchase Order.
      if (!saved) {
        return;
      }
      afterAddingPrice(deposits);
      // Pull the freshly-saved deposits + recomputed status back into
      // the V2 page state so the right-hand Order Summary's Status
      // badge flips PENDING CONFIRMATION → PAID immediately, the
      // Payments sidebar step recomputes as done, and the Balance Due
      // collapses to £0 without a manual reload. Without this refetch
      // the V2 page keeps the pre-save request object until the user
      // reloads the page (often twice — once for the request, once
      // for the deposits subobject).
      await refreshRequest?.();
    }
    // Sell paperwork only — the ORDER-side payments stay where they are
    // (they're terminal in those flows; advancing them would skip
    // user-driven sections like Commissions / Tracking). A PX slot renders
    // this molecule as that trade-in's OWN sell payment, so it advances too,
    // to its own suffixed Purchase Order rather than the static id.
    const pxSellIdx = step?.pxSellIdx;
    const isPxSlot = pxSellIdx !== null && pxSellIdx !== undefined;
    if (requestType === requestTypes.SELL || isPxSlot) {
      navigateTo?.(pxSellSectionId("purchase_order", pxSellIdx));
    }
  };

  useEffect(() => {
    if (requestType === requestTypes.STOCK) return;
    resolveListing();
  }, [
    request?.last_quote_id,
    request?.last_quote?.listing_id,
    request?.linked_listing_id,
    requestType,
    step.open,
  ]);

  const price = useMemo(() => {
    if (requestType === requestTypes.STOCK) {
      return request?.price_expectation;
    } else if (request?.last_quote?.price) {
      return request.last_quote.price;
    } else if (listing) {
      return listing?.price;
    }
    return 0;
  }, [listing, request, requestType]);

  // The Payments step (and its FX base) MUST follow the same source as
  // `price`: the quote drives the listing price, so the quote's currency
  // is the currency `price` is denominated in. Without this the step
  // defaults to GBP (StepPayments' `listingRate="gbp"`), which both
  // mislabels a non-GBP quote (e.g. "£9,300" for a $9,300 USD quote) AND
  // breaks settlement — a $9,300 payment gets FX-converted down to ~£6,880
  // against a £9,300 target and never clears. Mirror the `price` fallback
  // chain so the base always matches the amount being measured.
  const currency = useMemo(() => {
    if (requestType === requestTypes.STOCK) {
      return "gbp";
    } else if (request?.last_quote?.price) {
      return request.last_quote.currency ?? "gbp";
    } else if (listing) {
      return listing?.currency ?? "gbp";
    }
    return "gbp";
  }, [listing, request, requestType]);

  return (
    <Collapsible
      trigger={<SectionTrigger label={step.label} icon="payments" />}
      open={step.open}
      handleTriggerClick={() => toggleStep(idx)}
      testId="confirm-request"
    >
      <StepPayments
        listing={listing}
        onSave={onSave}
        price={price}
        listingRate={currency}
        request={request}
        xeroConnectionId={xeroConnectionId}
        xeroOrganisations={xeroOrganisations}
        // Native PO (no Xero POs on the plan, none issued in Xero yet).
        bankAccountNotNeeded={!hasXeroPo && !request?.xero_purchase_order_id}
        onXeroOrgChange={onSelectXeroOrg}
        xeroBrandingThemeId={xeroBrandingThemeId}
        onXeroBrandingChange={onSelectXeroBranding}
        supplierId={request?.client_id ?? request?.client?.id ?? null}
        xeroAddressId={xeroAddressId}
        onXeroAddressChange={onSelectXeroAddress}
      />

      {/* Voiding and re-issuing a live purchase order is confirmed in-app, and
          the dialog stays open to report Xero's reason if it refuses. */}
      {xeroOrgSwitch && (
        <SwitchXeroOrgModal
          open
          documentLabel="purchase order"
          fromLabel={orgLabel(xeroOrganisations, xeroOrgSwitch.fromId)}
          toLabel={orgLabel(xeroOrganisations, xeroOrgSwitch.toId)}
          submitting={xeroOrgSwitch.submitting}
          error={xeroOrgSwitch.error}
          onCancel={cancelXeroOrgSwitch}
          onConfirm={confirmXeroOrgSwitch}
        />
      )}
    </Collapsible>
  );
}
